import clsx from 'clsx'
import * as React from 'react'

import { makeStyles } from 'tss-react/mui'

import Typography from '@mui/material/Typography'

import MemoryIcon from '@mui/icons-material/Memory'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import ApplicationLogo from 'src/components/icons/ApplicationLogo'
import LiaLogo from 'src/components/icons/LiaLogo'
import DatabaseIcon from 'src/components/icons/DatabaseIcon'

export const useStyles = makeStyles()(() => ({
    root: {
        display: 'flex',
        flexDirection: 'row',
        height: '100vh',
        width: '100%',
        overflow: 'hidden',

        '@media (max-width: 900px)': {
            flexDirection: 'column',
            height: '100vh',
            overflowY: 'auto'
        }
    },

    leftContainer: {
        flex: 1,
        backgroundColor: '#000',
        padding: '80px 64px',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',

        '@media (max-width: 900px)': {
            order: 2,
            padding: '48px 32px'
        }
    },

    leftLogo: {
        width: 140,
        marginBottom: 48
    },

    leftContentInner: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'left'
    },

    leftTopArea: {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        paddingTop: 40
    },

    leftTitle: {
        fontSize: '2.0rem',
        fontWeight: 700,
        lineHeight: 1.2
    },

    leftTitleHighlight: {
        color: '#00E300'
    },

    leftSubtitle: {
        marginTop: 12,
        fontSize: '1rem',
        opacity: 0.8
    },

    cardsSubText: {
        fontSize: '0.80rem',
        opacity: 0.7
    },

    cardsGrid: {
        marginTop: 48,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 28,
        width: '100%'
    },

    infoCard: {
        backgroundColor: '#111',
        borderRadius: 14,
        padding: '28px 32px',
        border: '1px solid #1c1c1c',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        boxShadow: '0px 4px 20px rgba(0,0,0,0.35)',
        transition: '0.25s',
        '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0px 6px 28px rgba(0,0,0,0.45)'
        }
    },

    infoCardIconArea: {
        color: '#00E300'
    },

    footerText: {
        opacity: 0.5,
        fontSize: '0.80rem',
        marginTop: 32
    },

    rightContainer: {
        flex: 1,
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 48px',

        '@media (max-width: 900px)': {
            order: 1,
            width: '100%',
            padding: '32px 24px'
        }
    },

    rightLogo: {
        marginBottom: 24,
        display: 'flex',
        justifyContent: 'center'
    },

    formWrapper: {
        width: '100%',
        maxWidth: 380,
        display: 'flex',
        flexDirection: 'column',
        gap: 32
    }
}))

export type BaseSigninViewProps = {
    className?: string
    children?: React.ReactNode
}

export function BaseSigninView({ className, children }: BaseSigninViewProps) {
    const { classes } = useStyles()

    return (
        <div className={clsx(classes.root, className)}>
            <div className={classes.leftContainer}>
                <ApplicationLogo
                    className={classes.leftLogo}
                    place="sign-in"
                    sx={{
                        fontSize: '56px !important',
                        width: 'max-content !important'
                    }}
                />

                <div className={classes.leftTopArea}>
                    <div className={classes.leftContentInner}>
                        <Typography className={classes.leftTitle}>
                            Gestão inteligente de{' '}
                            <span className={classes.leftTitleHighlight}>negociações e parceiros.</span>
                        </Typography>

                        <Typography className={classes.leftSubtitle}>
                            Plataforma de gestão para o canal de atendimento via WhatsApp.
                        </Typography>

                        <div className={classes.cardsGrid}>
                            <div className={classes.infoCard}>
                                <DatabaseIcon className={classes.infoCardIconArea} fontSize="large" />
                                <Typography variant="h6" fontWeight={700}>
                                    Gestão de Parceiros
                                </Typography>
                                <Typography className={classes.cardsSubText}>
                                    Controle de acesso, perfis e filas de prioridade dos corretores
                                </Typography>
                            </div>

                            <div className={classes.infoCard}>
                                <MemoryIcon className={classes.infoCardIconArea} fontSize="large" />
                                <Typography variant="h6" fontWeight={700}>
                                    Canal WhatsApp
                                </Typography>
                                <Typography className={classes.cardsSubText} variant="body1">
                                    Consulta de CNPJs, disponibilidade para negociação e documentos de apresentação
                                </Typography>
                            </div>

                            <div className={classes.infoCard}>
                                <ShowChartIcon className={classes.infoCardIconArea} fontSize="large" />
                                <Typography variant="h6" fontWeight={700}>
                                    Relatórios Analíticos
                                </Typography>
                                <Typography className={classes.cardsSubText} variant="body1">
                                    Acompanhamento de negociações, prazos e desempenho dos parceiros
                                </Typography>
                            </div>
                        </div>
                    </div>
                </div>

                <Typography className={classes.footerText}>
                    © 2025 AG Capital. Todos os direitos reservados.
                </Typography>
            </div>

            <div className={classes.rightContainer}>
                <LiaLogo
                    className={classes.rightLogo}
                    textColor="#000"
                    size="medium"
                />
                <div className={classes.formWrapper}>{children}</div>
            </div>
        </div>
    )
}
